"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"

export interface TagSet {
  id: string
  name: string
  tags: string[]
  negativeTags: string[]
  createdAt: number
}

export interface TagPreset {
  id: string
  name: string
  tags: string[]
  negativeTags: string[]
  createdAt: number
}

export interface SetGroup {
  id: string
  name: string
  setIds: string[]
  createdAt: number
  lastUsed?: number
}

interface TagSetStore {
  sets: TagSet[]
  activeSetId: string
  tagPresets: TagPreset[]
  groups: SetGroup[]
  activeGroupId: string | null
  /** Not saved. While the next-tag swipe steps through tags, the search shows just this one tag of a set (see lib/tag-step.ts). */
  focus: { setId: string; tag: string } | null
  /** `activate: false` adds the set without switching to it (open in background). */
  createSet: (tags: string[], name?: string, activate?: boolean) => string
  removeSet: (id: string) => void
  updateSet: (id: string, tags: string[], negativeTags?: string[]) => void
  setActiveSet: (id: string) => void
  /** Make a set active and show only `tag` of it (null: show the whole set). */
  focusTag: (setId: string, tag: string | null) => void
  clearFocus: () => void
  addTagToSet: (id: string, tag: string) => void
  removeTagFromSet: (id: string, tag: string) => void
  renameSet: (id: string, name: string) => void
  saveTagPreset: (name: string, tags: string[], negativeTags?: string[]) => void
  deleteTagPreset: (id: string) => void
  loadTagPreset: (id: string) => TagPreset | undefined
  addNegativeTagToSet: (id: string, tag: string) => void
  removeNegativeTagFromSet: (id: string, tag: string) => void
  createGroup: (name?: string, setIds?: string[]) => string
  deleteGroup: (groupId: string) => void
  renameGroup: (groupId: string, name: string) => void
  addSetToGroup: (groupId: string, setId: string) => void
  removeSetFromGroup: (groupId: string, setId: string) => void
  setActiveGroup: (groupId: string | null) => void
  reorderSetsInGroup: (groupId: string, setIds: string[]) => void
  moveSetToGroup: (setId: string, fromGroupId: string | null, toGroupId: string | null) => void
}

// Helper function to generate unique set name (handles duplicates)
const generateUniqueSetName = (baseName: string, existingSets: TagSet[]): string => {
  const existingNames = existingSets.map(s => s.name)
  
  // If name doesn't exist, use it
  if (!existingNames.includes(baseName)) {
    return baseName
  }
  
  // Find next available suffix
  let suffix = 1
  while (existingNames.includes(`${baseName}_${suffix}`)) {
    suffix++
  }
  
  return `${baseName}_${suffix}`
}

// Helper function to generate set name based on tags
const generateSetName = (tags: string[], currentName: string, setIndex: number): string => {
  // If only 1 tag, use that tag as the name
  if (tags.length === 1) {
    return tags[0]
  }
  
  // If no tags, use default naming
  if (tags.length === 0) {
    return `Set ${setIndex}`
  }
  
  // If multiple tags, keep current name unless it's a default name or single tag name
  const isDefaultName = /^Set \d+$/.test(currentName)
  const isSingleTagName = tags.length > 1 && tags.includes(currentName)
  
  if (isDefaultName || isSingleTagName) {
    return `Set ${setIndex}`
  }
  
  return currentName
}

// Helper function to generate unique group name
const generateUniqueGroupName = (existingGroups: SetGroup[]): string => {
  const existingNames = existingGroups.map(g => g.name)
  let index = 1
  
  while (existingNames.includes(`Group ${index}`)) {
    index++
  }
  
  return `Group ${index}`
}

const createDefaultSet = (): TagSet => ({
  id: crypto.randomUUID(),
  name: "Set 1",
  tags: [],
  negativeTags: [],
  createdAt: Date.now(),
})

export const useTagSetStore = create<TagSetStore>()(
  persist(
    (set, get) => ({
      sets: [createDefaultSet()],
      activeSetId: "",
      tagPresets: [],
      groups: [],
      activeGroupId: null,
      focus: null,

      createSet: (tags: string[], name?: string, activate = true) => {
        const state = get()
        const setIndex = state.sets.length + 1
        const baseName = name || generateSetName(tags, `Set ${setIndex}`, setIndex)
        const uniqueName = generateUniqueSetName(baseName, state.sets)
        
        const newSet: TagSet = {
          id: crypto.randomUUID(),
          name: uniqueName,
          tags,
          negativeTags: [],
          createdAt: Date.now(),
        }
        set((state) => ({
          sets: [...state.sets, newSet],
          ...(activate && { activeSetId: newSet.id }),
        }))
        return newSet.id
      },

      removeSet: (id: string) => {
        const { sets, activeSetId } = get()
        if (sets.length === 1) return // Keep at least one set

        const newSets = sets.filter((s) => s.id !== id)
        const newActiveId = activeSetId === id ? newSets[0].id : activeSetId

        set((state) => ({
          sets: newSets,
          activeSetId: newActiveId,
          focus: state.focus && state.focus.setId === id ? null : state.focus,
        }))
      },

      updateSet: (id: string, tags: string[], negativeTags?: string[]) => {
        console.log('[v0] tag-set-store updateSet called', { id, tags, negativeTags, currentSets: get().sets })
        set((state) => {
          const setToUpdate = state.sets.find(s => s.id === id)
          console.log('[v0] tag-set-store - found set to update:', setToUpdate)

          // While one tag of the set is shown alone (next-tag swipe), the caller edited THAT one-tag list. Apply the edit to the
          // set's real tags: drop what the caller removed, add what it added. Stepping itself never changes the real tags.
          let nextTags = tags
          const focused = state.focus
          if (setToUpdate && focused && focused.setId === id && setToUpdate.tags.includes(focused.tag)) {
            const shown = [focused.tag]
            const added = tags.filter((t) => !shown.includes(t) && !setToUpdate.tags.includes(t))
            const removed = shown.filter((t) => !tags.includes(t))
            nextTags = [...setToUpdate.tags.filter((t) => !removed.includes(t)), ...added]
          }

          const newSets = state.sets.map((s, index) =>
            s.id === id
              ? {
                  ...s,
                  name: generateSetName(nextTags, s.name, index + 1),
                  tags: nextTags,
                  ...(negativeTags !== undefined && { negativeTags }),
                }
              : s,
          )
          console.log('[v0] tag-set-store updateSet - updated set:', newSets.find(s => s.id === id))
          // An edit ends the swipe's single-tag view; re-sending the very tag list being shown (not an edit) keeps it.
          const sameAsShown = !!focused && focused.setId === id && tags.length === 1 && tags[0] === focused.tag
          return { sets: newSets, focus: state.focus && state.focus.setId === id && !sameAsShown ? null : state.focus }
        })
        
        // Verify the update happened
        const afterState = get()
        console.log('[v0] tag-set-store updateSet - state after update:', { 
          sets: afterState.sets, 
          activeSetId: afterState.activeSetId,
          updatedSet: afterState.sets.find(s => s.id === id)
        })
      },

      setActiveSet: (id: string) => {
        set((state) => ({ activeSetId: id, focus: state.focus && state.focus.setId === id ? state.focus : null }))
      },

      focusTag: (setId: string, tag: string | null) => {
        set({ activeSetId: setId, focus: tag ? { setId, tag } : null })
      },

      clearFocus: () => {
        if (get().focus) set({ focus: null })
      },

      addTagToSet: (id: string, tag: string) => {
        set((state) => ({
          sets: state.sets.map((s, index) => {
            if (s.id === id && !s.tags.includes(tag)) {
              const newTags = [...s.tags, tag]
              return {
                ...s,
                tags: newTags,
                name: generateSetName(newTags, s.name, index + 1),
              }
            }
            return s
          }),
        }))
      },

      removeTagFromSet: (id: string, tag: string) => {
        set((state) => ({
          sets: state.sets.map((s, index) => {
            if (s.id === id) {
              const newTags = s.tags.filter((t) => t !== tag)
              return {
                ...s,
                tags: newTags,
                name: generateSetName(newTags, s.name, index + 1),
              }
            }
            return s
          }),
          focus: state.focus && state.focus.setId === id && state.focus.tag === tag ? null : state.focus,
        }))
      },

      renameSet: (id: string, name: string) => {
        set((state) => ({
          sets: state.sets.map((s) => (s.id === id ? { ...s, name } : s)),
        }))
      },

      saveTagPreset: (name: string, tags: string[], negativeTags: string[] = []) => {
        const newPreset: TagPreset = {
          id: crypto.randomUUID(),
          name,
          tags,
          negativeTags,
          createdAt: Date.now(),
        }
        set((state) => ({
          tagPresets: [...state.tagPresets, newPreset],
        }))
      },

      deleteTagPreset: (id: string) => {
        set((state) => ({
          tagPresets: state.tagPresets.filter((p) => p.id !== id),
        }))
      },

      loadTagPreset: (id: string) => {
        return get().tagPresets.find((p) => p.id === id)
      },

      addNegativeTagToSet: (id: string, tag: string) => {
        set((state) => ({
          sets: state.sets.map((s) =>
            s.id === id && !s.negativeTags.includes(tag) ? { ...s, negativeTags: [...s.negativeTags, tag] } : s,
          ),
        }))
      },

      removeNegativeTagFromSet: (id: string, tag: string) => {
        set((state) => ({
          sets: state.sets.map((s) =>
            s.id === id ? { ...s, negativeTags: s.negativeTags.filter((t) => t !== tag) } : s,
          ),
        }))
      },

      createGroup: (name?: string, setIds: string[] = []) => {
        const state = get()
        const groupName = name || generateUniqueGroupName(state.groups)
        
        const newGroup: SetGroup = {
          id: crypto.randomUUID(),
          name: groupName,
          setIds,
          createdAt: Date.now(),
          lastUsed: Date.now(),
        }
        set((state) => ({
          groups: [...state.groups, newGroup],
          activeGroupId: newGroup.id,
        }))
        return newGroup.id
      },

      deleteGroup: (groupId: string) => {
        set((state) => ({
          groups: state.groups.filter((g) => g.id !== groupId),
          activeGroupId: state.activeGroupId === groupId ? null : state.activeGroupId,
        }))
      },

      renameGroup: (groupId: string, name: string) => {
        set((state) => ({
          groups: state.groups.map((g) => (g.id === groupId ? { ...g, name } : g)),
        }))
      },

      addSetToGroup: (groupId: string, setId: string) => {
        set((state) => ({
          groups: state.groups.map((g) =>
            g.id === groupId && !g.setIds.includes(setId) ? { ...g, setIds: [...g.setIds, setId] } : g,
          ),
        }))
      },

      removeSetFromGroup: (groupId: string, setId: string) => {
        set((state) => ({
          groups: state.groups.map((g) =>
            g.id === groupId ? { ...g, setIds: g.setIds.filter((id) => id !== setId) } : g,
          ),
        }))
      },

      setActiveGroup: (groupId: string | null) => {
        set((state) => ({
          activeGroupId: groupId,
          groups: state.groups.map((g) => (g.id === groupId ? { ...g, lastUsed: Date.now() } : g)),
        }))
      },

      reorderSetsInGroup: (groupId: string, setIds: string[]) => {
        set((state) => ({
          groups: state.groups.map((g) => (g.id === groupId ? { ...g, setIds } : g)),
        }))
      },

      moveSetToGroup: (setId: string, fromGroupId: string | null, toGroupId: string | null) => {
        set((state) => {
          let newGroups = [...state.groups]

          // Remove from old group
          if (fromGroupId) {
            newGroups = newGroups.map((g) =>
              g.id === fromGroupId ? { ...g, setIds: g.setIds.filter((id) => id !== setId) } : g,
            )
          }

          // Add to new group
          if (toGroupId) {
            newGroups = newGroups.map((g) =>
              g.id === toGroupId && !g.setIds.includes(setId) ? { ...g, setIds: [...g.setIds, setId] } : g,
            )
          }

          return { groups: newGroups }
        })
      },
    }),
    {
      name: "tag-sets",
      // The focus is a moment-to-moment view of the swipe, not something to bring back after a reload.
      partialize: ({ focus: _focus, ...rest }) => rest as unknown as TagSetStore,
      onRehydrateStorage: () => (state) => {
        console.log('[v0] tag-set-store rehydrating', { state })
        // Set active set to first one if not set
        if (state && !state.activeSetId && state.sets.length > 0) {
          console.log('[v0] tag-set-store: setting activeSetId to first set')
          state.activeSetId = state.sets[0].id
        }
        // Fix corrupted state: if activeSetId points to non-existent set, reset it
        if (state && state.activeSetId && state.sets.length > 0) {
          const activeSetExists = state.sets.some(s => s.id === state.activeSetId)
          if (!activeSetExists) {
            console.log('[v0] tag-set-store: activeSetId points to non-existent set, resetting to first set')
            state.activeSetId = state.sets[0].id
          }
        }
      },
    },
  ),
)
